import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { promptAPI } from '@/lib/api';
import { ArrowLeft, Upload, Loader2, Download, Sparkles, X, Users, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const PhotoComposer = () => {
  const navigate = useNavigate();
  const fileInputRefs = [useRef(null), useRef(null), useRef(null)];
  
  const [images, setImages] = useState([null, null, null]); // Up to 3 images
  const [imageBase64s, setImageBase64s] = useState([null, null, null]);
  const [backgroundPrompt, setBackgroundPrompt] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGroupMode, setIsGroupMode] = useState(false); // For combining group photos

  const handleFileUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        const newImages = [...images];
        const newBase64s = [...imageBase64s];
        newImages[index] = event.target.result;
        newBase64s[index] = base64;
        setImages(newImages);
        setImageBase64s(newBase64s);
        toast.success(`Image ${index + 1} uploaded!`);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    const newImages = [...images];
    const newBase64s = [...imageBase64s];
    newImages[index] = null;
    newBase64s[index] = null;
    setImages(newImages);
    setImageBase64s(newBase64s);
  };

  const getUploadedCount = () => {
    return images.filter(img => img !== null).length;
  };

  const buildPrompt = () => {
    const count = getUploadedCount();
    let prompt = '';
    
    if (isGroupMode) {
      // Group mode - for combining photos that already have multiple people
      prompt = `IMPORTANT: Each uploaded image may contain MULTIPLE people (group photos). `;
      prompt += `Combine ALL the people from ALL the uploaded images into ONE single group photograph. `;
      prompt += `If an image has 3 people in it, include all 3 of those people. `;
      prompt += `Preserve EVERY person's face, features, and identity exactly as shown. `;
      prompt += `The final image should show everyone together as one large group. `;
    } else {
      // Normal mode - each image is one person
      prompt = `Combine the ${count} people from the uploaded images into a single high-quality photograph. `;
      prompt += `Make it look completely natural and realistic, as if they were actually photographed together. `;
      prompt += `Preserve each person's face, features, and identity exactly as shown in their original photos. `;
    }
    
    if (backgroundPrompt) {
      prompt += `Place them in this setting: ${backgroundPrompt}. `;
    } else {
      prompt += `Use a professional studio background. `;
    }
    
    if (additionalDetails) {
      prompt += additionalDetails;
    }
    
    prompt += ` High quality, professional photography, realistic lighting, natural poses.`;
    
    return prompt;
  };

  const handleGenerate = async () => {
    const uploadedCount = getUploadedCount();
    if (uploadedCount < 2) {
      toast.error('Please upload at least 2 images to combine');
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    
    try {
      const prompt = buildPrompt();
      const validBase64s = imageBase64s.filter(b64 => b64 !== null);
      
      const result = await promptAPI.composePhotos(prompt, validBase64s);
      if (result.success) {
        setGeneratedImage({
          data: result.image_data,
          mimeType: result.mime_type,
          description: result.description
        });
        toast.success('Photo composed successfully!');
      }
    } catch (error) {
      console.error('Compose error:', error);
      toast.error('Failed to compose photo. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    try {
      // Convert base64 to blob for better handling of large files
      const byteCharacters = atob(generatedImage.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: generatedImage.mimeType });
      
      // Try using the download attribute
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `terrylb-composed-${Date.now()}.png`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Use setTimeout to ensure the link is in the DOM
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Image downloading...');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download. Try right-clicking the image and selecting "Save image as..."');
    }
  };

  const handleOpenInNewTab = () => {
    if (!generatedImage) return;
    try {
      const byteCharacters = atob(generatedImage.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: generatedImage.mimeType });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Image opened in new tab - right-click to save');
    } catch (error) {
      console.error('Open error:', error);
      toast.error('Failed to open image');
    }
  };

  const imageLabels = ['Person 1 (You)', 'Person 2 (Friend/Celebrity)', 'Person 3 (Optional)'];
  const imagePlaceholders = [
    'Upload your photo',
    'Upload friend or celebrity photo',
    'Add another person (optional)'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <nav className="px-6 py-4 bg-white/80 backdrop-blur-sm border-b-4 border-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Users className="h-7 w-7 text-orange-600" />
          <h1 className="text-xl font-bold">Photo Composer</h1>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center">
          <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Photo Composer</span>
        </h2>
        <p className="text-lg text-center mb-12 text-gray-700">Combine multiple photos into one - meet celebrities, reunite with friends!</p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Upload & Settings */}
          <div className="space-y-6">
            {/* Image Uploads */}
            <Card className="p-6 bg-white border-4 border-black card-shadow">
              <h3 className="text-xl font-bold mb-4">1. Upload Photos to Combine</h3>
              <p className="text-sm text-gray-600 mb-4">Upload 2-3 photos of people you want to combine into one image</p>
              
              <div className="grid grid-cols-3 gap-4">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="relative">
                    <p className="text-xs font-bold mb-2 text-center truncate">{imageLabels[index]}</p>
                    <div 
                      className={`border-4 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-orange-500 transition-colors aspect-square flex flex-col items-center justify-center ${
                        index === 2 ? 'border-gray-200 bg-gray-50' : 'border-gray-300'
                      }`}
                      onClick={() => fileInputRefs[index].current?.click()}
                    >
                      {images[index] ? (
                        <div className="relative w-full h-full">
                          <img 
                            src={images[index]} 
                            alt={`Upload ${index + 1}`} 
                            className="w-full h-full object-cover rounded"
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <ImagePlus className={`h-8 w-8 mx-auto ${index === 2 ? 'text-gray-300' : 'text-gray-400'}`} />
                          <p className="text-xs text-gray-500">{imagePlaceholders[index]}</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRefs[index]}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(index, e)}
                      className="hidden"
                    />
                  </div>
                ))}
              </div>
              
              <div className="mt-4 text-center">
                <span className={`text-sm font-medium ${getUploadedCount() >= 2 ? 'text-green-600' : 'text-orange-600'}`}>
                  {getUploadedCount()}/3 photos uploaded {getUploadedCount() >= 2 ? '✓' : '(minimum 2 required)'}
                </span>
              </div>

              {/* Group Mode Toggle */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isGroupMode}
                    onChange={(e) => setIsGroupMode(e.target.checked)}
                    className="w-5 h-5 rounded border-2 border-blue-500 accent-blue-600"
                  />
                  <div>
                    <span className="font-bold text-blue-800">Group Photo Mode</span>
                    <p className="text-xs text-blue-600">Enable this if your images already contain multiple people (e.g., combining two 3-person photos into a 6-person photo)</p>
                  </div>
                </label>
              </div>
            </Card>

            {/* Background & Details */}
            <Card className="p-6 bg-white border-4 border-black card-shadow">
              <h3 className="text-xl font-bold mb-4">2. Choose Background & Style</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2">Background/Setting</label>
                  <Input 
                    value={backgroundPrompt}
                    onChange={(e) => setBackgroundPrompt(e.target.value)}
                    placeholder="e.g., red carpet event, beach sunset, Paris Eiffel Tower, concert stage"
                    className="border-2 border-black"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold mb-2">Additional Details (Optional)</label>
                  <Textarea 
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    placeholder="e.g., Both people smiling, casual outfit, arm around shoulder, holding awards..."
                    className="border-2 border-black min-h-[80px]"
                  />
                </div>

                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    💡 <strong>Tip:</strong> For best results, use clear face photos with good lighting. Celebrity photos work great!
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleGenerate}
                disabled={isGenerating || getUploadedCount() < 2}
                className="w-full mt-6 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-lg py-6 text-lg font-bold hover:from-orange-700 hover:to-amber-700"
              >
                {isGenerating ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Composing Photo (30-60s)...</>
                ) : (
                  <><Sparkles className="mr-2 h-5 w-5" /> ✨ Compose Photo</>
                )}
              </Button>
            </Card>
          </div>

          {/* Right Panel - Result */}
          <div>
            <Card className="p-6 bg-white border-4 border-black card-shadow h-full">
              <h3 className="text-xl font-bold mb-4">3. Your Composed Photo</h3>
              
              {generatedImage ? (
                <div className="space-y-4">
                  <img 
                    src={`data:${generatedImage.mimeType};base64,${generatedImage.data}`}
                    alt="Composed"
                    className="w-full rounded-lg border-2 border-gray-200"
                  />
                  {generatedImage.description && (
                    <p className="text-sm text-gray-600 italic">{generatedImage.description}</p>
                  )}
                  <Button 
                    onClick={handleDownload}
                    className="w-full bg-green-500 text-white rounded-lg font-bold hover:bg-green-600"
                  >
                    <Download className="mr-2 h-4 w-4" /> Download Photo
                  </Button>
                  <Button 
                    onClick={handleOpenInNewTab}
                    variant="outline"
                    className="w-full mt-2 border-2 border-black font-bold"
                  >
                    Open in New Tab (then right-click to save)
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Users className="h-16 w-16 mb-4 opacity-50" />
                  <p className="text-center">Upload photos and click compose to create your combined photo!</p>
                  <p className="text-center text-sm mt-2">Perfect for photos with celebrities, friends, or family!</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoComposer;
